import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { CONFIG, authFetch, authHeaders } from '../utils/config';
import { trackPaymentConversion } from '../utils/analytics';
import { useContent } from '../hooks/useContent';

const AccountPage = () => {
  const { isDark } = useTheme();
  const { content, loading: contentLoading } = useContent('accountPage');
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFeedback, setPasswordFeedback] = useState({ type: '', message: '' });
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchAccount = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await authFetch(`${CONFIG.BASE_URL}/account`, {
          method: 'GET',
          headers: authHeaders(),
        });

        if (!response.ok) {
          throw new Error(`Impossible de récupérer le compte (${response.status})`);
        }

        const data = await response.json();

        if (isMounted) {
          setAccount(data);
        }
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError.message || content.error.defautl);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchAccount();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasSubscription =
    account && typeof account.subscription === 'object' && account.subscription !== null;

  const subscriptionInfo = hasSubscription ? account.subscription.info : null;
  const usageLimits = hasSubscription
    ? account.subscription.usage_and_limits || account.subscription.qr_code_usage_and_limits
    : null;
  const analytics = hasSubscription ? account.subscription.analytics : null;

  const formattedCreatedAt = useMemo(() => {
    if (!account?.created_at) return '—';
    return new Date(account.created_at).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [account]);

  const formattedUpdatedAt = useMemo(() => {
    if (!account?.updated_at) return '—';
    return new Date(account.updated_at).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [account]);

  const handleOpenManagementPortal = async () => {
    setPortalLoading(true);
    setActionMessage('');
    try {
      const response = await authFetch(`${CONFIG.BASE_URL}/subscription-management-page/`, {
        method: 'GET',
        headers: authHeaders(),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || `Action impossible (${response.status})`);
      }

      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error(content.errors.portal.missingUrl);
      }
    } catch (portalError) {
      setActionMessage(portalError.message || content.errors.portal.stripe);
    } finally {
      setPortalLoading(false);
    }
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();

    const trimmedCurrentPassword = currentPassword.trim();
    const trimmedNewPassword = newPassword.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    if (!trimmedCurrentPassword || !trimmedNewPassword || !trimmedConfirmPassword) {
      setPasswordFeedback({
        type: 'error',
        message: content.security.feedback.missingFields,
      });
      return;
    }

    if (trimmedNewPassword.length < 8) {
      setPasswordFeedback({
        type: 'error',
        message: content.security.feedback.passwordTooShort,
      });
      return;
    }

    if (trimmedNewPassword !== trimmedConfirmPassword) {
      setPasswordFeedback({
        type: 'error',
        message: content.security.feedback.passwordMismatch,
      });
      return;
    }

    setIsSubmittingPassword(true);
    setPasswordFeedback({ type: '', message: '' });

    try {
      const response = await fetch(`${CONFIG.BASE_URL}/change-password`, {
        method: 'POST',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: trimmedCurrentPassword,
          new_password: trimmedNewPassword,
        }),
      });

      const isJson = (response.headers.get('content-type') || '').includes('application/json');
      const data = isJson ? await response.json() : {};

      if (!response.ok) {
        throw new Error(data?.error || 'Impossible de mettre à jour le mot de passe.');
      }

      setPasswordFeedback({
        type: 'success',
        message: 'Votre mot de passe a été mis à jour.',
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (passwordError) {
      setPasswordFeedback({
        type: 'error',
        message: passwordError.message || 'Une erreur est survenue. Merci de réessayer.',
      });
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const containerClass = `min-h-screen ${isDark ? 'bg-dark-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`;
  const cardClass = `rounded-2xl border ${isDark ? 'bg-dark-800/70 border-dark-700' : 'bg-white border-gray-200'}`;
  const subtitleClass = `text-sm font-medium uppercase tracking-wide ${
    isDark ? 'text-primary-300/80' : 'text-primary-600'
  }`;
  const passwordInputClass = isDark
    ? 'w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500'
    : 'w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

  useEffect(() => {
    if (!hasSubscription) {
      return;
    }
    const raw = localStorage.getItem('quikr_pending_payment_conversion');
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (parsed.timestamp && Date.now() - parsed.timestamp > 1000 * 60 * 60 * 24) {
        localStorage.removeItem('quikr_pending_payment_conversion');
        return;
      }
      trackPaymentConversion({
        value: parsed.value || 0,
        currency: parsed.currency || 'EUR',
      });
    } catch (conversionError) {
      console.error('Unable to process payment conversion tracking', conversionError);
    } finally {
      localStorage.removeItem('quikr_pending_payment_conversion');
    }
  }, [hasSubscription]);

  const formatNumber = (value) => (value == null ? '—' : Number(value).toLocaleString('fr-FR'));

  if (contentLoading || !content) {

    return (
      <div className={`pt-16 min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
  <div className={containerClass}>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      <header className="space-y-2">
        <p className={subtitleClass}>{content.sections.header.subtitle}</p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{content.sections.header.title}</h1>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              {content.sections.header.description}
            </p>
          </div>
          {hasSubscription && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleOpenManagementPortal}
                disabled={portalLoading}
                className="px-4 py-2 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {portalLoading
                  ? content.sections.header.opening
                  : content.sections.header.manageButton}
              </button>
            </div>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center space-x-3 text-primary-400">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span>{content.loading}</span>
          </div>
        </div>
      ) : error ? (
        <div className={`${cardClass} p-8 text-center`}>
          <p className="text-red-400 font-medium">{error}</p>
          <p className="text-sm text-gray-500 mt-2">{content.error.fallback}</p>
        </div>
      ) : (
        <>
          {actionMessage && (
            <div className={`${cardClass} p-4 border-red-500/40 text-red-300`}>
              {actionMessage}
            </div>
          )}

          <section className={`${cardClass} p-6 space-y-6`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className={subtitleClass}>{content.sections.identity.title}</p>
                <h2 className="text-2xl font-semibold">
                  {account?.user_name || content.sections.identity.fallbackName}
                </h2>
                <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                  {account?.user_mail}
                </p>
                <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500">{content.sections.identity.labels.created}</dt>
                    <dd className="font-medium">{formattedCreatedAt}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">{content.sections.identity.labels.updated}</dt>
                    <dd className="font-medium">{formattedUpdatedAt}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <p className={subtitleClass}>{content.sections.organisation.title}</p>
                <div className={`rounded-xl border ${
                  isDark ? 'border-dark-600 bg-dark-900/40' : 'border-gray-200 bg-gray-50'
                } p-4`}>
                  <p className="text-sm text-gray-500 mt-4">Organisation</p>
                  <p className="font-medium">
                    {account?.organisation_id || content.sections.organisation.fallback}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {hasSubscription ? (
            <>
              <section className={`${cardClass} p-6 space-y-4`}>
                <div>
                  <p className={subtitleClass}>{content.sections.subscription.title}</p>
                  <h3 className="text-xl font-semibold">{subscriptionInfo?.name}</h3>
                </div>
              </section>

              <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={`${cardClass} p-6 space-y-4`}>
                  <p className={subtitleClass}>{content.sections.usage.title}</p>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between border-b border-dark-600/40 pb-2">
                      <span>{content.sections.usage.limits.aiQrActive}</span>
                      <span className="font-semibold">
                        {formatNumber(usageLimits?.max_qr_code_ai_count)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-dark-600/40 pb-2">
                      <span>{content.sections.usage.limits.aiQrMonthly}</span>
                      <span className="font-semibold">
                        {formatNumber(usageLimits?.max_qr_code_ai_generation_try_monthly_limit)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{content.sections.usage.limits.classicQrActive}</span>
                      <span className="font-semibold">
                        {formatNumber(usageLimits?.max_qr_code_standard_count)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`${cardClass} p-6 space-y-4`}>
                  <p className={subtitleClass}>{content.sections.analytics.title}</p>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between border-b border-dark-600/40 pb-2">
                      <span>{content.sections.analytics.fields.history}</span>
                      <span className="font-semibold">
                        {formatNumber(analytics?.analytics_dashboard_history)} {content.sections.analytics.days}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-dark-600/40 pb-2">
                      <span>
                        {analytics?.analytics_dashboard_map_countries === 1
                          ? content.sections.analytics.fields.mapCountries
                          : content.sections.analytics.fields.mapCountries_plural.replace(
                              '{{count}}',
                              formatNumber(analytics?.analytics_dashboard_map_countries || 0)
                            )}
                      </span>
                      <span>{content.sections.analytics.available}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-dark-600/40 pb-2">
                      <span>{content.sections.analytics.fields.devices}</span>
                      <span className="font-semibold">
                        {analytics?.analytics_dashboard_devices
                          ? content.sections.analytics.available
                          : content.sections.analytics.notIncluded}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-dark-600/40 pb-2">
                      <span>{content.sections.analytics.fields.cities}</span>
                      <span className="font-semibold">
                        {analytics?.analytics_dashboard_map_cities
                          ? content.sections.analytics.available
                          : content.sections.analytics.notIncluded}
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <section className={`${cardClass} p-6`}>
              <p className={subtitleClass}>{content.sections.subscription.title}</p>
              <div className="mt-2 p-4 rounded-xl bg-black/20 border border-white/5 text-gray-400">
                {account?.subscription}
              </div>
            </section>
          )}

          <section className={`${cardClass} p-6 space-y-5`}>
            <div>
              <p className={subtitleClass}>{content.sections.security.title}</p>
              <h3 className="text-xl font-semibold">{content.sections.security.subtitle}</h3>
              <p className={isDark ? 'text-gray-400 text-sm' : 'text-gray-600 text-sm'}>
                {content.sections.security.description}
              </p>
            </div>

            {passwordFeedback.message && (
              <div
                className={`px-4 py-3 rounded-lg text-sm border ${
                  passwordFeedback.type === 'success'
                    ? 'bg-green-900/30 border-green-600 text-green-200'
                    : 'bg-red-900/30 border-red-600 text-red-200'
                }`}
              >
                {passwordFeedback.message}
              </div>
            )}

            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <label
                  htmlFor="account-current-password"
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {content.sections.security.form.currentPassword.label}
                </label>
                <input
                  id="account-current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  className={passwordInputClass}
                  placeholder={content.sections.security.form.currentPassword.placeholder}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="account-new-password"
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {content.sections.security.form.newPassword.label}
                </label>
                <input
                  id="account-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  className={passwordInputClass}
                  placeholder={content.sections.security.form.newPassword.placeholder}
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="account-confirm-password"
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {content.sections.security.form.confirmPassword.label}
                </label>
                <input
                  id="account-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className={passwordInputClass}
                  placeholder={content.sections.security.form.confirmPassword.placeholder}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isSubmittingPassword}
                className="w-full flex justify-center py-3 rounded-lg border border-transparent font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmittingPassword
                  ? content.sections.security.form.submitting
                  : content.sections.security.form.submit}
              </button>
            </form>
          </section>
        </>
      )}
    </div>
  </div>
);
};

export default AccountPage;
